import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "../services/api";
import { BsFiletypePdf } from "react-icons/bs";
import { CiCirclePlus } from "react-icons/ci";
import { FaEdit, FaTrashAlt } from "react-icons/fa";
import "./InvoiceReferencePage.css"; // reuse invoice reference styles

const EstimateReferencePage = () => {
  const { id: businessId } = useParams();
  //const navigate = useNavigate();

  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [taxRate, setTaxRate] = useState("");
  const [estimates, setEstimates] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [entryFormData, setEntryFormData] = useState({});
  const [showEntryPopup, setShowEntryPopup] = useState(false);
  const [popupMode, setPopupMode] = useState("add");
  const [activeEstimateId, setActiveEstimateId] = useState(null);
  const [activeEntryId, setActiveEntryId] = useState(null);
  const [selectedVendorId, setSelectedVendorId] = useState("");
  const [selectedIds, setSelectedIds] = useState([]); // estimate row selection

  useEffect(() => {
    (async () => {
      try {
        const vendorRes = await api.get(`/business/${businessId}/vendors`);
        setVendors(vendorRes.data);

        const productRes = await api.get(`/business/${businessId}/products`);
        setProducts(productRes.data);

        const businessRes = await api.get(`/businesses/${businessId}`);
        if (businessRes.data.sales_tax_rate) {
          setTaxRate(businessRes.data.sales_tax_rate);
        }
      } catch (err) {
        console.error("Error fetching estimate setup:", err);
      }
    })();
  }, [businessId]);

  const fetchEstimates = useCallback(async () => {
    try {
      // your list endpoint that filters is_estimate=1
      const res = await api.get(`/business/${businessId}/estimates`);
      setEstimates(res.data);
    } catch (err) {
      console.error("Error loading estimates", err);
    }
  }, [businessId]);

  useEffect(() => { fetchEstimates(); }, [fetchEstimates]);


  useEffect(() => {
  const onRefresh = () => fetchEstimates();
  window.addEventListener("estimates-refresh", onRefresh);
  return () => window.removeEventListener("estimates-refresh", onRefresh);
}, [fetchEstimates]);


  const toggleExpandRow = async (estimateId) => {
    if (expandedRows[estimateId]) {
      const copy = { ...expandedRows };
      delete copy[estimateId];
      setExpandedRows(copy);
    } else {
      try {
        // reuse the SAME entries endpoint as invoices:
        const res = await api.get(`/invoices/${estimateId}/entries`);
        setExpandedRows(prev => ({ ...prev, [estimateId]: res.data }));
      } catch (err) {
        console.error("Error loading entries", err);
      }
    }
  };

  const saveEditedEntry = async (estimateId, entryId) => {
    try {
      const isManual = !!entryFormData.is_manual;
      const price = parseFloat(entryFormData.price);
      const quantity = parseFloat(entryFormData.quantity);
      const hasPrice = !isNaN(price);
      const hasQty = !isNaN(quantity);
      const hasAmount = entryFormData.amount !== undefined && entryFormData.amount !== "" && !isNaN(parseFloat(entryFormData.amount));

      let amount = 0;
      if (isManual) {
        if (hasPrice && hasQty) {
          amount = parseFloat((price * quantity).toFixed(2));
        } else if (hasAmount) {
          amount = parseFloat(parseFloat(entryFormData.amount).toFixed(2));
        } else {
          alert("Amount is required for manual entry.");
          return;
        }
      } else {
        if (!hasPrice || !hasQty) {
          alert("Both price and quantity are required for product entries.");
          return;
        }
        amount = parseFloat((price * quantity).toFixed(2));
      }

      let sales_tax = 0;
      if (!isManual && entryFormData.product_id) {
        const prod = products.find(p => p.id === Number(entryFormData.product_id));
        if (prod?.taxable) {
          const taxRateFloat = parseFloat(taxRate || 0);
          sales_tax = parseFloat(((price * quantity) * taxRateFloat / 100).toFixed(2));
        }
      }

      const selectedProduct = products.find(p => p.id === Number(entryFormData.product_id));

      const payload = {
        ...entryFormData,
        description: isManual ? entryFormData.description : (selectedProduct?.item || null),
        type: "expense", // enforce expense-only in payload
        price: hasPrice ? price : null,
        quantity: hasQty ? quantity : null,
        amount,
        sales_tax,
      };

      await api.put(`/invoices/${estimateId}/entries/${entryId}`, payload); // same endpoint
      setEditingEntryId(null);
      const updated = await api.get(`/invoices/${estimateId}/entries`);
      setExpandedRows(prev => ({ ...prev, [estimateId]: updated.data }));
      await fetchEstimates();
    } catch (err) {
      console.error("Failed to update entry:", err);
      alert("Error updating entry.");
    }
  };

  const deleteEntry = async (estimateId, entryId) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      await api.delete(`/invoice-entries/${entryId}`); // same delete endpoint
      const updated = await api.get(`/invoices/${estimateId}/entries`);
      setExpandedRows(prev => ({ ...prev, [estimateId]: updated.data }));
      await fetchEstimates();
    } catch (err) {
      console.error("Failed to delete:", err);
      alert("Failed to delete.");
    }
  };

  const handleSaveNewEntry = async (estimateId, stayOpen = false) => {
    const { entry_date, is_manual, product_id, description, quantity, price, amount } = entryFormData;
    const selectedProduct = products.find(p => p.id === Number(product_id));

    if (is_manual) {
      if (!description || (!amount && !(price && quantity))) {
        alert("Missing required fields for manual entry.");
        return;
      }
    } else {
      if (!product_id || !quantity) {
        alert("Missing required fields for product entry.");
        return;
      }
    }

    const priceNum = parseFloat(price || 0);
    const qtyNum = parseFloat(quantity || 0);
    let computedAmount = isNaN(priceNum * qtyNum) ? parseFloat(amount || 0) : parseFloat((priceNum * qtyNum).toFixed(2));

    let salesTax = 0;
    if (!is_manual && selectedProduct?.taxable) {
      const base = (isNaN(priceNum) || price === "" ? parseFloat(selectedProduct.price || 0) : priceNum) * (qtyNum || 0);
      salesTax = parseFloat(((base * parseFloat(taxRate || 0)) / 100).toFixed(2));
    }

    const payload = {
      entry_date: entry_date || null,
      is_manual: !!is_manual,
      product_id: is_manual ? null : product_id,
      description: is_manual ? description : (selectedProduct?.item || null),
      type: "expense", // enforce expense-only
      quantity: isNaN(qtyNum) ? null : qtyNum,
      price: isNaN(priceNum) ? null : priceNum,
      amount: isNaN(computedAmount) ? 0 : computedAmount,
      sales_tax: salesTax,
    };

    try {
      await api.post(`/invoices/${estimateId}/entries`, payload); // same add endpoint
      const updated = await api.get(`/invoices/${estimateId}/entries`);
      setExpandedRows(prev => ({ ...prev, [estimateId]: updated.data }));
      await fetchEstimates();

      if (stayOpen) {
        setEntryFormData({
          is_manual: false,
          entry_date: "",
          product_id: "",
          description: "",
          price: "",
          quantity: "",
          amount: "",
        });
      } else {
        setShowEntryPopup(false);
      }
    } catch (err) {
      console.error("Error adding entry:", err);
      alert("Failed to add entry.");
    }
  };


  const handleGeneratePdf = async (invoiceId, businessId) => {
          try {
            
            const response = await api.get(
              `/invoices/${invoiceId}/print`,
              { responseType: "blob"
            });
  
            const blob = new Blob([response.data], { type: "application/pdf" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `estimate-${invoiceId}.pdf`;
            link.click();
          } catch (err) {
            console.error("Error generating PDF:", err);
            alert("Failed to generate PDF. Please try again.");
          }
        };

  const handleConvertToInvoices = async () => {
    try {
      await api.post(`/estimates/convert`, { business_id: Number(businessId), estimate_ids: selectedIds });
      alert("Converted to invoices.");
      setSelectedIds([]);
      await fetchEstimates();
    } catch (err) {
      console.error("Convert failed:", err);
      alert("Failed to convert selected estimates.");
    }
  };

  const filtered = selectedVendorId
    ? estimates.filter(e => String(e.vendor_id) === String(selectedVendorId))
    : estimates;

  const allOnPageSelected = filtered.length > 0 && filtered.every(e => selectedIds.includes(e.id));

  return (
    <div className="inv-ref-container">

      {/* Filter */}
      <div className="inv-ref-filter">
      <button
          className="add-as-inv"
          disabled={selectedIds.length === 0}
          onClick={handleConvertToInvoices}
          title={selectedIds.length === 0 ? "Select at least one estimate" : "Convert to invoice(s)"}
        >
          Add as Invoice
        </button>
        <select id="vendor-filter" value={selectedVendorId} onChange={e => setSelectedVendorId(e.target.value)}>
          <option value="">All Customers</option>
          {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
      </div>

      

      
  <table className="inv-ref-invoice-table inv-ref-header-table">
        <thead>
          <tr>
            <th className="ch-box-head">
              <input
                type="checkbox"
                checked={allOnPageSelected}
                onChange={(e) => {
                  if (e.target.checked) {
                    const ids = filtered.map(f => f.id);
                    setSelectedIds(prev => Array.from(new Set([...prev, ...ids])));
                  } else {
                    setSelectedIds(prev => prev.filter(id => !filtered.some(f => f.id === id)));
                  }
                }}
              />
              
            </th>
            <th className="cust-head">Customer</th>
            <th className="type-head">Type</th>
            <th className="date-head">Date</th>
            <th className="act-head">Actions</th>
          </tr>
        </thead>
        </table>


        <div className="inv-table-scroll">
  <table className="inv-ref-invoice-table inv-ref-body-table">
        <tbody>
          {filtered.map(est => (
            <React.Fragment key={est.id}>
              <tr className="inv-ref-clickable-row" onClick={() => toggleExpandRow(est.id)}>
                <td className="chk-est-page" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(est.id)}
                    onChange={(e) => {
                      if (e.target.checked) setSelectedIds(prev => [...prev, est.id]);
                      else setSelectedIds(prev => prev.filter(id => id !== est.id));
                    }}
                  />
                </td>
                <td className="cust-head">{est.vendor_name}</td>
                <td className="type-head">{est.invoice_type}</td>
                <td className="date-head">{est.invoice_date ? new Date(est.invoice_date).toISOString().slice(0, 10) : ""}</td>
                <td className="act-head">
                          <button className="pdf-btn" onClick={() => handleGeneratePdf(est.id)}><BsFiletypePdf /></button>
                          {/* <button
                  onClick={() => handleEmailInvoice(inv.id)}
                  className="inv-email-btn"
                >
                  📧 Email Invoice
                </button> */}
                
                
                          
                        </td>
              </tr>

              {expandedRows[est.id] && (
                <tr className="expanded-invoice">
                  <td colSpan={8}>
                    <table className={`inv-ref-subtable ${est.invoice_type === 'open' ? 'with-date' : 'no-date'}`}>
                      <thead>
                        <tr>
                          {est.invoice_type === "open" && <th className="sub-date">Date</th>}
                          <th className="sub-desc">Description</th>
                          <th className="sub-qty">Quantity</th>
                          <th className="sub-price">Price</th>
                          <th className="sub-amount">Amount</th>
                          <th className="sub-type">Type</th>
                          <th className="sub-action">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {expandedRows[est.id].map(entry => {
                          const isEditing = editingEntryId === entry.id;
                          return (
                            <tr key={entry.id}>
                              <td colSpan="7">
                                {isEditing ? (
                                  <div className="inv-ref-entry-box">
                                    {est.invoice_type === "open" && (
                                      <div>
                                        <label>Date</label>
                                        <input
                                          type="date"
                                          value={entryFormData.entry_date || ""}
                                          onChange={(e) => setEntryFormData({ ...entryFormData, entry_date: e.target.value })}
                                        />
                                      </div>
                                    )}

                                    {entryFormData.is_manual ? (
                                      <>
                                        <input
                                          type="text"
                                          placeholder="Description"
                                          value={entryFormData.description || ""}
                                          onChange={(e) => setEntryFormData({ ...entryFormData, description: e.target.value })}
                                        />
                                        {/* No type select; expense-only */}
                                        <input
                                          type="number"
                                          placeholder="Price"
                                          value={entryFormData.price || ""}
                                          onChange={(e) => {
                                            const price = parseFloat(e.target.value || 0);
                                            const quantity = parseFloat(entryFormData.quantity || 0);
                                            setEntryFormData({
                                              ...entryFormData,
                                              price,
                                              amount: !isNaN(price * quantity) ? (price * quantity).toFixed(2) : "",
                                            });
                                          }}
                                        />
                                        <input
                                          type="number"
                                          placeholder="Qty"
                                          value={entryFormData.quantity || ""}
                                          onChange={(e) => {
                                            const quantity = parseFloat(e.target.value || 0);
                                            const price = parseFloat(entryFormData.price || 0);
                                            setEntryFormData({
                                              ...entryFormData,
                                              quantity,
                                              amount: !isNaN(price * quantity) ? (price * quantity).toFixed(2) : "",
                                            });
                                          }}
                                        />
                                        <input
                                          type="number"
                                          placeholder="Amount"
                                          value={entryFormData.amount || ""}
                                          onChange={(e) => setEntryFormData({ ...entryFormData, amount: e.target.value })}
                                        />
                                      </>
                                    ) : (
                                      <>
                                        <select
                                          value={entryFormData.product_id || ""}
                                          onChange={(e) => {
                                            const prod = products.find(p => p.id === Number(e.target.value));
                                            const price = prod ? parseFloat(prod.price) : 0;
                                            const quantity = parseFloat(entryFormData.quantity || 0);
                                            const amount = !isNaN(price * quantity) ? (price * quantity).toFixed(2) : "";
                                            setEntryFormData({
                                              ...entryFormData,
                                              product_id: e.target.value,
                                              price,
                                              amount,
                                            });
                                          }}
                                        >
                                          <option value="">Select Product</option>
                                          {products.map(prod => (
                                            <option key={prod.id} value={prod.id}>{prod.item}</option>
                                          ))}
                                        </select>
                                        <input
                                          type="number"
                                          placeholder="Qty"
                                          value={entryFormData.quantity || ""}
                                          onChange={(e) => {
                                            const quantity = parseFloat(e.target.value || 0);
                                            const price = parseFloat(entryFormData.price || 0);
                                            const amount = !isNaN(price * quantity) ? (price * quantity).toFixed(2) : "";
                                            setEntryFormData({ ...entryFormData, quantity, amount });
                                          }}
                                        />
                                        <input
                                          type="number"
                                          placeholder="Amount"
                                          value={entryFormData.amount || ""}
                                          onChange={(e) => setEntryFormData({ ...entryFormData, amount: e.target.value })}
                                        />
                                      </>
                                    )}

                                    <button className="save-invoice" onClick={() => saveEditedEntry(est.id, entry.id)}>Save</button>
                                    <button className="cancel-inv" onClick={() => setEditingEntryId(null)}>Cancel</button>
                                  </div>
                                ) : (
                                  <table className="inv-ref-subtable">
                                    <tbody>
                                      <tr>
                                        {est.invoice_type === "open" && (
                                          <td className="sub-date">
                                            {entry.entry_date ? new Date(entry.entry_date).toLocaleDateString() : "N/A"}
                                          </td>
                                        )}
                                        <td className="sub-desc">{entry.description || `Product #${entry.product_id}`}</td>
                                        <td className="sub-qty">{entry.quantity}</td>
                                        <td className="sub-price">{entry.price}</td>
                                        <td className="sub-amount">{entry.amount}</td>
                                        <td className="sub-type">expense</td>
                                        <td className="sub-action">
                                          <button
                                            className="ed-btn"
                                            onClick={() => {
                                              setPopupMode("edit");
                                              setActiveEstimateId(est.id);
                                              setActiveEntryId(entry.id);
                                              setEntryFormData({
                                                ...entry,
                                                is_manual: entry.is_manual === 1 || entry.is_manual === "1" || entry.is_manual === true,
                                                entry_date: entry.entry_date ? new Date(entry.entry_date).toISOString().slice(0, 10) : "",
                                              });
                                              setShowEntryPopup(true);
                                            }}
                                          >
                                            <FaEdit />
                                          </button>
                                          <button className="del-btn" onClick={() => deleteEntry(est.id, entry.id)}>
                                            <FaTrashAlt />
                                          </button>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                )}
                              </td>
                            </tr>
                          );
                        })}

                        <tr>
                          <td colSpan="8">
                            <button
                              onClick={() => {
                                setPopupMode("add");
                                setActiveEstimateId(est.id);
                                setActiveEntryId(null);
                                setEntryFormData({
                                  is_manual: false,
                                  entry_date: "",
                                  description: "",
                                  product_id: "",
                                  quantity: "",
                                  price: "",
                                  amount: "",
                                });
                                setShowEntryPopup(true);
                              }}
                            >
                              <CiCirclePlus /> Add Entry
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    <div className="total-row-invoice">
                      {est.invoice_type === "open" && <td></td>}
                      <div className="sub-total">Subtotal:</div>
                      <div style={{ fontWeight: "bold", marginLeft: "10px" }}>
                        {expandedRows[est.id].reduce((acc, curr) => acc + parseFloat(curr.amount || 0), 0).toFixed(2)}
                      </div>
                      <div className="sub-tax">Tax:</div>
                      <div style={{ fontWeight: "bold", marginLeft: "10px" }}>
                        {expandedRows[est.id].reduce((acc, curr) => acc + parseFloat(curr.sales_tax || 0), 0).toFixed(2)}
                      </div>
                      <div className="total-invoice">Total:</div>
                      <div style={{ fontWeight: "bold", marginLeft: "10px" }}>
                        {expandedRows[est.id].reduce((acc, curr) => acc + parseFloat(curr.amount || 0) + parseFloat(curr.sales_tax || 0), 0).toFixed(2)}
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      </div>

      {/* Shared popup (no type selector) */}
      {showEntryPopup && (
        <>
          <div className="overlay-invoice" onClick={() => setShowEntryPopup(false)}></div>
          <div className="popup-invoice entry-popup">
            <h3>{popupMode === "edit" ? "Edit Estimate Entry" : "Add Estimate Entry"}</h3>

            <div className="entry-mode-switch">
              <button
                type="button"
                className={entryFormData.is_manual ? '' : 'active'}
                onClick={() =>
                  setEntryFormData({
                    ...entryFormData,
                    is_manual: false,
                    description: "",
                    product_id: "",
                    price: "",
                    quantity: "",
                    amount: "",
                  })
                }
              >
                Product
              </button>
              <button
                type="button"
                className={entryFormData.is_manual ? 'active' : ''}
                onClick={() =>
                  setEntryFormData({
                    ...entryFormData,
                    is_manual: true,
                    description: "",
                    product_id: "",
                    price: "",
                    quantity: "",
                    amount: "",
                  })
                }
              >
                Manual
              </button>
            </div>

            {estimates.find(e => e.id === activeEstimateId)?.invoice_type === "open" && (
              <div className="inv-date-entry-ref">
                <label>Date</label>
                <input
                  type="date"
                  value={entryFormData.entry_date || ""}
                  onChange={(e) => setEntryFormData({ ...entryFormData, entry_date: e.target.value })}
                />
              </div>
            )}

            {entryFormData.is_manual ? (
              <>
                <input
                  type="text"
                  placeholder="Description"
                  value={entryFormData.description || ""}
                  onChange={(e) => setEntryFormData({ ...entryFormData, description: e.target.value })}
                />
                <input
                  type="number"
                  placeholder="Price"
                  value={entryFormData.price || ""}
                  onChange={(e) => {
                    const price = parseFloat(e.target.value || 0);
                    const qty = parseFloat(entryFormData.quantity || 0);
                    setEntryFormData({
                      ...entryFormData,
                      price,
                      amount: !isNaN(price * qty) ? (price * qty).toFixed(2) : "",
                    });
                  }}
                />
                <input
                  type="number"
                  placeholder="Quantity"
                  value={entryFormData.quantity || ""}
                  onChange={(e) => {
                    const qty = parseFloat(e.target.value || 0);
                    const price = parseFloat(entryFormData.price || 0);
                    setEntryFormData({
                      ...entryFormData,
                      quantity: qty,
                      amount: !isNaN(price * qty) ? (price * qty).toFixed(2) : "",
                    });
                  }}
                />
              </>
            ) : (
              <>
                <select
                  value={entryFormData.product_id || ""}
                  onChange={(e) => {
                    const selected = products.find(p => p.id === Number(e.target.value));
                    const price = selected ? parseFloat(selected.price) : 0;
                    const qty = parseFloat(entryFormData.quantity || 0);
                    setEntryFormData({
                      ...entryFormData,
                      product_id: e.target.value,
                      price,
                      amount: !isNaN(price * qty) ? (price * qty).toFixed(2) : "",
                    });
                  }}
                >
                  <option value="">Select Product</option>
                  {products.map(prod => <option key={prod.id} value={prod.id}>{prod.item}</option>)}
                </select>
                <input
                  type="number"
                  placeholder="Quantity"
                  value={entryFormData.quantity || ""}
                  onChange={(e) => {
                    const qty = parseFloat(e.target.value || 0);
                    const price = parseFloat(entryFormData.price || 0);
                    setEntryFormData({
                      ...entryFormData,
                      quantity: qty,
                      amount: !isNaN(price * qty) ? (price * qty).toFixed(2) : "",
                    });
                  }}
                />
              </>
            )}

            <input
              type="number"
              placeholder="Amount"
              value={entryFormData.amount || ""}
              onChange={(e) => setEntryFormData({ ...entryFormData, amount: e.target.value })}
            />

            <div className="popup-actions">
              <button className="save-invoice" onClick={async () => {
                if (popupMode === "add") {
                  await handleSaveNewEntry(activeEstimateId, false);
                } else {
                  await saveEditedEntry(activeEstimateId, activeEntryId);
                }
                setShowEntryPopup(false);
              }}>
                Save
              </button>
              {popupMode === "add" && (
                <button className="add-another-inv" onClick={async () => {
                  await handleSaveNewEntry(activeEstimateId, true);
                }}>
                  Save & Add Another
                </button>
              )}
              <button className="cancel-inv" onClick={() => setShowEntryPopup(false)}>Cancel</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EstimateReferencePage;
